<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import LaisCard from 'dashboard/components-next/LaisCard.vue';
import SalesPropertyWarningAlert from './SalesPropertyWarningAlert.vue';
import {
  formatCurrencyInput,
  parseCurrencyInput,
} from 'dashboard/components-next/input/inputUtils';
import {
  daysOptions,
  paymentOptions,
} from '../composables/salesPropertyFormOptions';
import SalesPropertyReferenceField from './SalesPropertyReferenceField.vue';
const props = defineProps({
  profile: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['update']);
const { t } = useI18n();
const dismissed = ref({ minimum: false, laudemio: false });
const options = computed(() => daysOptions(t));
const payments = computed(() => paymentOptions(t));
const paymentLabel = computed(() => {
  const selectedPayments = props.profile.forma_pagamento || [];
  if (selectedPayments.includes('2') && selectedPayments.includes('3')) {
    return t('SALES_PROPERTY.DEADLINE.FINANCING_WITH_FGTS');
  }
  let selected = selectedPayments[0];
  if (selectedPayments.includes('2')) selected = '2';
  if (selectedPayments.includes('3')) selected = '3';
  return (
    payments.value.find(option => option.value === selected)?.label ||
    t('SALES_PROPERTY.COMMON.NO_DATA')
  );
});
const minimumDays = computed(() => {
  if (!props.profile.forma_pagamento?.some(item => ['2', '3'].includes(item)))
    return null;
  return props.profile.tipo_dias === '2' ? 65 : 45;
});
const warningVisible = computed(
  () => Boolean(minimumDays.value) && !dismissed.value.minimum
);
const minimumDaysLabel = computed(() => {
  const suffix =
    props.profile.tipo_dias === '2'
      ? t('SALES_PROPERTY.OPTIONS.DAYS.CALENDAR_SHORT')
      : t('SALES_PROPERTY.OPTIONS.DAYS.BUSINESS_SHORT');
  return `${minimumDays.value} ${suffix}`;
});
const laudemioDaysLabel = computed(() =>
  props.profile.tipo_dias === '2'
    ? t('SALES_PROPERTY.DEADLINE.LAUDEMIO_CALENDAR_DAYS')
    : t('SALES_PROPERTY.DEADLINE.LAUDEMIO_BUSINESS_DAYS')
);
const laudemioVisible = computed(
  () =>
    props.profile.laudemios?.some(item => item.tipo_laudemio === '2') &&
    !dismissed.value.laudemio
);
const update = (field, value) => emit('update', field, value);
const penaltyValue = computed(() => {
  const value = props.profile.valoMulta;
  if (value === null || value === undefined || value === '') return '';
  const source = String(value);
  if (typeof value === 'string' && !/[R$]/.test(source)) return source;
  const parsed = /[R$,]/.test(source)
    ? parseCurrencyInput(source)
    : Number(source);
  return parsed === null || Number.isNaN(parsed)
    ? ''
    : formatCurrencyInput(parsed);
});
const setPenalty = value =>
  update('valoMulta', parseCurrencyInput(value) ?? '');
</script>

<template>
  <LaisCard
    density="flush"
    class="self-start !border-0 !shadow-sm"
    data-test="sales-property-deadline-section"
  >
    <div class="p-8 font-roboto">
      <h2
        class="font-lato text-[32px] font-bold leading-[39px] text-[#464F53] max-[700px]:text-[24px] max-[700px]:leading-[29px]"
      >
        {{ t('SALES_PROPERTY.DEADLINE.TITLE') }}
      </h2>
      <fieldset
        class="my-[44px] flex flex-wrap gap-x-6 gap-y-3"
        :aria-label="t('SALES_PROPERTY.ARIA.DAYS')"
        data-test="sales-property-day-options"
      >
        <label
          v-for="option in options"
          :key="option.value"
          class="flex min-h-12 items-center gap-2 rounded-[10px] py-2 pr-9 pl-[22px] text-base max-[700px]:min-h-[58px]"
          :class="
            props.profile.tipo_dias === option.value
              ? 'bg-[#EDF8F7] font-bold text-[#01988C]'
              : 'text-[#464F53]'
          "
        >
          <span class="relative grid size-5 shrink-0 place-items-center">
            <input
              type="radio"
              name="sales-property-days"
              :value="option.value"
              :checked="props.profile.tipo_dias === option.value"
              class="sr-only"
              @change="update('tipo_dias', option.value)"
            />
            <span
              class="grid size-5 place-items-center rounded-full border-2"
              :class="
                props.profile.tipo_dias === option.value
                  ? 'border-[#01988C]'
                  : 'border-[#5D696F]'
              "
            >
              <span
                v-if="props.profile.tipo_dias === option.value"
                class="size-[10px] rounded-full bg-[#01988C]"
              />
            </span>
          </span>
          {{ option.label }}
        </label>
      </fieldset>
      <div class="w-[441px]" data-test="sales-property-days-field">
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.prazo)"
          :label="t('SALES_PROPERTY.DEADLINE.DAYS')"
          :error="errors.prazo"
          required
        >
          <input
            :value="props.profile.prazo"
            type="number"
            min="1"
            :placeholder="t('SALES_PROPERTY.DEADLINE.DAYS_PLACEHOLDER')"
            class="!h-8 !w-full !rounded-none !border-0 !border-b !border-[#009F94] !bg-transparent !p-0 !text-base !font-bold !leading-6 !text-[#009F94] !outline-none !ring-0 placeholder:!text-[#A7B7BE]"
            @input="update('prazo', $event.target.value)"
          />
        </SalesPropertyReferenceField>
      </div>
      <p class="mt-[20px] mb-[44px] text-xl text-lais-text-body">
        {{ t('SALES_PROPERTY.DEADLINE.PENALTY_TITLE') }}
      </p>
      <div class="mb-[44px] w-[441px]" data-test="sales-property-penalty-field">
        <SalesPropertyReferenceField
          :filled="Boolean(props.profile.valoMulta)"
          :label="t('SALES_PROPERTY.DEADLINE.PENALTY')"
        >
          <input
            :value="penaltyValue"
            :placeholder="t('SALES_PROPERTY.DEADLINE.PENALTY_PLACEHOLDER')"
            inputmode="decimal"
            class="!h-8 !w-full !rounded-none !border-0 !border-b !border-[#009F94] !bg-transparent !p-0 !text-base !font-bold !leading-6 !text-[#009F94] !outline-none !ring-0 placeholder:!text-[#A7B7BE]"
            @input="setPenalty($event.target.value)"
          />
        </SalesPropertyReferenceField>
      </div>
      <div class="grid w-full gap-[5px]">
        <SalesPropertyWarningAlert
          v-if="warningVisible"
          test-id="deadline-minimum-warning"
          :dismiss-label="t('SALES_PROPERTY.DEADLINE.DISMISS_WARNING')"
          @dismiss="dismissed.minimum = true"
        >
          {{ t('SALES_PROPERTY.DEADLINE.MINIMUM_WARNING_PREFIX') }}
          <strong>
            {{ minimumDaysLabel
            }}{{ t('SALES_PROPERTY.DEADLINE.MINIMUM_WARNING_MIDDLE') }}
            {{ paymentLabel }}{{ t('SALES_PROPERTY.DEADLINE.WARNING_END') }}
          </strong>
        </SalesPropertyWarningAlert>
        <SalesPropertyWarningAlert
          v-if="laudemioVisible"
          test-id="deadline-laudemio-warning"
          :dismiss-label="t('SALES_PROPERTY.DEADLINE.DISMISS_WARNING')"
          @dismiss="dismissed.laudemio = true"
        >
          {{ t('SALES_PROPERTY.DEADLINE.LAUDEMIO_WARNING_PREFIX') }}
          <strong>
            {{ laudemioDaysLabel
            }}{{ t('SALES_PROPERTY.DEADLINE.WARNING_END') }}
          </strong>
        </SalesPropertyWarningAlert>
      </div>
      <slot name="footer" />
    </div>
  </LaisCard>
</template>
